/**
 * In-memory session state for E2EE.
 *
 * Holds the decrypted family key(s) and current device keypair for the active
 * session. Both stores (`familyStore`, `eventsStore`) read from here when they
 * need to encrypt outgoing writes, since they don't have access to the React
 * EncryptionContext.
 *
 * On logout: call `clearSession()` to drop in-memory keys. Device keypair in
 * IndexedDB is NOT cleared — it persists across sessions so the same browser
 * stays trusted.
 */

import type { DeviceKeypair } from "./deviceKeys";

let currentDevice: DeviceKeypair | null = null;
const familyKeysInMemory = new Map<string, CryptoKey>();

export const setCurrentDevice = (kp: DeviceKeypair | null): void => {
  currentDevice = kp;
};

export const getCurrentDevice = (): DeviceKeypair | null => currentDevice;

export const setFamilyKeyInMemory = (familyId: string, key: CryptoKey): void => {
  familyKeysInMemory.set(familyId, key);
};

export const getFamilyKeyInMemory = (familyId: string): CryptoKey | null =>
  familyKeysInMemory.get(familyId) ?? null;

export const hasFamilyKeyInMemory = (familyId: string): boolean =>
  familyKeysInMemory.has(familyId);

export const clearFamilyKeysInMemory = (): void => {
  familyKeysInMemory.clear();
};

export const clearSession = (): void => {
  currentDevice = null;
  familyKeysInMemory.clear();
};
